import React, { useState, useEffect, useMemo } from 'react';
import logoCp from '../logo-cp.svg';
import { FileText, LayoutDashboard, Package, ShoppingCart, BarChart2, TrendingUp, Users, RefreshCw, MessageSquare, Wrench, Mail, Settings, ChevronDown, ChevronRight, Megaphone, LogOut, Store, Warehouse, Phone, DollarSign, ClipboardCheck, Camera, User, Sparkles, Inbox, Image, Clock, Target, BookOpen, ClipboardList, Search, X, MapPin, Calculator, Headphones, Truck, ListOrdered, Lightbulb, Star, CreditCard, ShieldAlert, Home, BellRing, Bus } from 'lucide-react';
import { getCurrentUser } from '../services/auth';
import { neonQuery, navFavorites } from '../services/api';
import { PortNavBadge } from './PortStatusBadge';

// Roles: Admin, Manager, Procurement, Fulfillment, Customer Service, Marketing, HR, Accounting
const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, page: 'dashboard' }, // everyone
  // Ask: questions, reports, screenshots, emails -> answered from the KB + read-only data.
  // Everyone gets it; what it can READ is decided server-side (lib/ask-access.js).
  { id: 'ask', label: 'Ask', icon: Sparkles, page: 'ask' },
  // Every admin portal feature and how far it has moved here (Azure exit WS8). Everyone sees
  // it: the features that have not moved have no page of their own to carry a badge.
  { id: 'port-status', label: 'Portal Move Status', icon: ListOrdered, page: 'port-status' },
  // The order alert list (Carman and Matt, Oct 26, 2026): shipping and order heads-ups in
  // one list instead of their own emails. The server allows the same roles (cp-alerts.js).
  { id: 'alerts', label: 'Order Alerts', icon: BellRing, page: 'alerts', allowedRoles: ['Admin', 'Manager', 'IT', 'Fulfillment'] },
  {
    id: 'fulfillment', label: 'Fulfillment', icon: Package,
    allowedRoles: ['Admin', 'Fulfillment'],
    children: [
      { id: 'shipping-today', label: 'Shipping Today', page: 'shipping-today' },
      { id: 'remaining-today', label: 'Remaining Today', page: 'remaining-today' },
      { id: 'products', label: 'Product List', page: 'products' },
      { id: 'product-alerts', label: 'Product Alerts', page: 'product-alerts' },
      { id: 'gift-certificates', label: 'Gift Certificates', page: 'gift-certificates' },
      { id: 'packing-costs', label: 'Packing Costs', page: 'packing-costs' },
      { id: 'ops-planner', label: 'Ops Planner', page: 'ops-planner' },
    ]
  },
  {
    id: 'orders', label: 'Orders', icon: ShoppingCart,
    allowedRoles: ['Admin', 'Fulfillment', 'Customer Service'],
    children: [
      { id: 'order-details', label: 'Order Details', page: 'order-details' },
      // The new order screen CS is trying on real orders (src/pages/OrderDesk.js). Same roles as
      // Order Details: it inherits the Orders section's.
      { id: 'order-desk', label: 'Order Desk (beta)', page: 'order-desk' },
      { id: 'placed-today', label: 'Placed Today', page: 'placed-today' },
      { id: 'on-hold', label: 'On Hold', page: 'on-hold' },
      { id: 'pending-charge', label: 'Pending Charge/Credit', page: 'pending-charge' },
      { id: 'open-claims', label: 'Open Claim Orders', page: 'open-claims' },
      { id: 'fraud-alert', label: 'Fraud Alert', page: 'fraud-alert' },
      { id: 'ak-hi', label: 'AK/HI Order', page: 'ak-hi' },
      { id: 'address-validations', label: 'Address Validations', page: 'address-validations' },
      { id: 'access-point-report', label: 'UPS Access Point Orders', page: 'access-point-report' },
      { id: 'large-ground', label: 'Large Ground Orders', page: 'large-ground' },
      { id: 'large-ground-ap', label: 'Large Ground Orders (AP)', page: 'large-ground-ap' },
      { id: 'physical-gc', label: 'Physical Gift Certs', page: 'physical-gc' },
      // Reviews of every UPDATED confirmation the admin sends (lib/order-update-review.js).
      { id: 'order-updates', label: 'Order Updates', page: 'order-updates', allowedRoles: ['Admin'] },
      // The shipping rulebook (lib/shipping-rules.js). Everyone reads; Admin and Manager change.
      { id: 'shipping-rules', label: 'Shipping Rules', page: 'shipping-rules' },
    ]
  },
  // Top level, not under Orders or Accounting: Rachael's CS team keeps the log and Greg and Mercedes
  // post its totals to QuickBooks, and neither group can see the other's section. The writes go to
  // production (dbo.Chargebacks via the Common BE), so the server needs a signed-in rep regardless.
  { id: 'chargebacks', label: 'Chargebacks', icon: CreditCard, page: 'chargebacks',
    allowedRoles: ['Admin', 'Manager', 'Customer Service', 'Accounting'] },
  // Rachael's end-of-month recap (Greg, Oct 27, 2026): built from CPx and sent from her own Outlook,
  // replacing the spreadsheets one section at a time. Beside Chargebacks, which feeds it.
  { id: 'cr-monthly-report', label: 'Monthly Report', icon: FileText, page: 'cr-monthly-report',
    allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
  // C&C Fraud Watch (Rachael, 2026-10-20): fraud flags on Crab & Cruise accounts and the alerts when
  // one buys again or re-registers. Beside Chargebacks because that is where the flags come from.
  // In CPx, not CNCx, because staff cannot sign in to CNCx.
  { id: 'cnc-fraud', label: 'C&C Fraud Watch', icon: ShieldAlert, page: 'cnc-fraud',
    allowedRoles: ['Admin', 'Manager'] },
  // C&C bus group payment plans (Rachael, Oct 27, 2026): replaces her spreadsheet. Customer Service
  // runs the plans (Rachael, Ashley, Linda), so it sits with the other CS pages, not under Marketing.
  { id: 'cnc-bus-groups', label: 'C&C Bus Groups', icon: Bus, page: 'cnc-bus-groups',
    allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
  {
    id: 'reports', label: 'Reports', icon: BarChart2,
    allowedRoles: ['Admin', 'Accounting', 'Marketing'],
    children: [
      { id: 'claim-orders', label: 'Claim Orders', page: 'claim-orders' },
      { id: 'shipping-insurance', label: 'Shipping Insurance', page: 'shipping-insurance' },
      { id: 'profit-report', label: 'Profit Report', page: 'profit-report' },
      { id: 'profit-report-ap', label: 'Profit Report (AP)', page: 'profit-report-ap' },
      { id: 'profit-analysis', label: 'Profit Analysis', page: 'profit-analysis' },
      { id: 'profit-per-box', label: 'Profit Per Box', page: 'profit-per-box' },
      { id: 'daily-ppb', label: 'Daily PPB', page: 'daily-ppb' },
      { id: 'ppb-segments', label: 'PPB Segments', page: 'ppb-segments' },
      { id: 'sales-report', label: 'Sales Report', page: 'sales-report' },
      { id: 'sales-report-ap', label: 'Sales Report (AP)', page: 'sales-report-ap' },
      { id: 'daily-summary', label: 'Orders Summary', page: 'daily-summary' },
      { id: 'category-sales', label: 'Category Sales', page: 'category-sales' },
      { id: 'hard-crab-calendar', label: 'Hard Crab Calendar', page: 'hard-crab-calendar' },
      { id: 'hard-crab-calendar-ap', label: 'Hard Crab Calendar (AP)', page: 'hard-crab-calendar-ap' },
      { id: 'hard-crab-costs', label: 'Hard Crab Costs', page: 'hard-crab-costs' },
      { id: 'shipping-report', label: 'Shipping Report', page: 'shipping-report' },
      { id: 'shipping-margin', label: 'Shipping Margin', page: 'shipping-margin' },
      { id: 'order-method', label: 'Order Method', page: 'order-method' },
      { id: 'new-vs-returning', label: 'New vs Returning', page: 'new-vs-returning' },
      { id: 'new-vs-returning-ap', label: 'New vs Returning (AP)', page: 'new-vs-returning-ap' },
      { id: 'referring-ad', label: 'Referring Ad', page: 'referring-ad' },
      { id: 'referring-ad-ap', label: 'Referring Ad (AP)', page: 'referring-ad-ap' },
      { id: 'state-breakdown', label: 'State Breakdown', page: 'state-breakdown' },
      { id: 'unfinished-orders', label: 'Unfinished Orders', page: 'unfinished-orders' },
      { id: 'unfinished-orders-ap', label: 'Unfinished Orders (AP)', page: 'unfinished-orders-ap' },
      { id: 'order-data-export', label: 'Order Data Export', page: 'order-data-export' },
      { id: 'refunds-reships', label: 'Refunds vs Reships', page: 'refunds-reships' },
      { id: 'product-report', label: 'Product Report', page: 'product-report' },
      { id: 'vip-cash-report', label: 'VIP Cash', page: 'vip-cash-report' },
    ]
  },
  {
    id: 'profit-margin', label: 'Profit Margin Report', icon: TrendingUp,
    allowedRoles: ['Admin', 'Accounting'],
    children: [
      { id: 'profit-margin-orders', label: 'By Order', page: 'profit-margin-orders' },
      { id: 'profit-margin-by-product', label: 'By Product', page: 'profit-margin-by-product' },
      { id: 'profit-margin-detail', label: 'Detail Report', page: 'profit-margin-detail' },
    ]
  },
  {
    id: 'customers', label: 'Customers', icon: Users,
    allowedRoles: ['Admin', 'Customer Service', 'Marketing'],
    children: [
      { id: 'customer-search', label: 'Customer Search', page: 'customer-search' },
      { id: 'customer-analysis', label: 'Customer Analysis', page: 'customer-analysis' },
      { id: 'vip-search', label: 'VIP Search', page: 'vip-search' },
      { id: 'vip-club', label: 'VIP Club', page: 'vip-club' },
      { id: 'vip-health', label: 'VIP Health', page: 'vip-health' },
      { id: 'vip-optouts', label: 'VIP Opt-Outs', page: 'vip-optouts' },
      { id: 'linked-accounts', label: 'Linked Accounts', page: 'linked-accounts' },
      { id: 'customer-categorization', label: 'Categorization', page: 'customer-categorization', allowedRoles: ['Admin', 'Manager'] },
    ]
  },
  {
    id: 'reconciliation', label: 'UPS', icon: RefreshCw,
    allowedRoles: ['Admin', 'Accounting', 'Fulfillment'],
    children: [
      { id: 'reconciliation-bills', label: 'Bills', page: 'reconciliation-bills' },
      { id: 'pending-activity', label: 'Pending Activity', page: 'pending-activity' },
      { id: 'ups-contract', label: 'Contract Pricing', page: 'ups-contract' },
      { id: 'ups-contract-eval', label: 'Contract Evaluation', page: 'ups-contract-eval' },
      { id: 'reconciliation-shipping', label: 'Shipping Reconciliation', page: 'reconciliation-shipping' },
      { id: 'reconciliation-delivery', label: 'Delivery', page: 'reconciliation-delivery' },
      { id: 'ups-claims', label: 'Claims', page: 'ups-claims' },
    ]
  },
  {
    id: 'feedback', label: 'Feedback', icon: MessageSquare,
    allowedRoles: ['Admin', 'Customer Service'],
    children: [
      { id: 'feedback-summary', label: 'Summary / Details', page: 'feedback-summary' },
      { id: 'feedback-by-category', label: 'By Category', page: 'feedback-by-category' },
      { id: 'feedback-by-product', label: 'By Product', page: 'feedback-by-product' },
      { id: 'feedback-follow-up', label: 'Email Follow Up', page: 'feedback-follow-up' },
      { id: 'feedback-cs-resolution', label: 'CS Resolution', page: 'feedback-cs-resolution' },
      { id: 'feedback-low-score-alerts', label: 'Low Score Alerts', page: 'feedback-low-score-alerts' },
      { id: 'reviews', label: 'Customer Reviews (Neon)', page: 'reviews' },
      { id: 'google-review-replies', label: 'Google Review Replies', page: 'google-review-replies' },
    ]
  },
  // Improvements sits directly under Feedback because that is where the evidence for
  // most of its items lives, and it is deliberately NOT under Procurement: two of the
  // five opening items are Matt Sr's, but the rest belong to the packing floor, to
  // checkout and to grading, and a cross-department list filed under one department is
  // invisible to the others.
  {
    id: 'improvements', label: 'Improvements', icon: ClipboardList, page: 'improvements',
    allowedRoles: ['Admin', 'Manager', 'Procurement', 'Fulfillment', 'Customer Service'],
  },
  {
    id: 'procurement', label: 'Procurement', icon: Wrench,
    allowedRoles: ['Admin', 'Manager', 'Procurement', 'Fulfillment'],
    children: [
      { id: 'procurement-brain', label: 'Procurement Brain', page: 'procurement-brain', allowedRoles: ['Admin', 'Manager', 'Procurement'] },
      { id: 'competitor-pricing', label: 'Competitor Pricing', page: 'competitor-pricing', allowedRoles: ['Admin', 'Manager', 'Procurement'] },
      { id: 'waste-log', label: 'Waste Log', page: 'waste-log', allowedRoles: ['Admin', 'Manager', 'Procurement', 'Fulfillment'] },
      { id: 'crab-cake-planner', label: 'Crab Cake Planner', page: 'crab-cake-planner', allowedRoles: ['Admin', 'Manager', 'Procurement', 'Fulfillment'] },
      {
        id: 'hcd', label: 'HCD', allowedRoles: ['Admin', 'Procurement'],
        children: [
          { id: 'yield-report', label: 'Yield Report', page: 'yield-report' },
          { id: 'yield-sheet', label: 'Yield Sheet', page: 'yield-sheet' },
          { id: 'vendor-summary', label: 'Vendor Summary', page: 'vendor-summary' },
          { id: 'demand-planning', label: 'Demand Planning', page: 'demand-planning' },
        ]
      },
      {
        id: 'inventory', label: 'Product Inventory', icon: Warehouse, allowedRoles: ['Admin', 'Procurement', 'Fulfillment'],
        children: [
          { id: 'inventory-summary', label: 'CPx Catch', page: 'inventory' },
          { id: 'pricing', label: 'Pricing', page: 'pricing' },
          { id: 'cost-mapping', label: 'Cost Mapping', page: 'cost-mapping' },
          { id: 'inventory-limit-alerts', label: 'Inventory Limit Alerts', page: 'inventory-limit-alerts' },
          { id: 'bc-product-setup', label: 'BC Product Setup', page: 'bc-product-setup', allowedRoles: ['Admin'] },
          { id: 'price-history', label: 'Price History', page: 'price-history' },
          { id: 'price-comparison', label: 'Price Comparison', page: 'price-comparison' },
        ]
      },
    ]
  },
  {
    id: 'marketing', label: 'Marketing', icon: Megaphone,
    allowedRoles: ['Admin', 'Marketing'],
    children: [
      // The North Star dashboard — leads the section because every other item
      // here is judged by whether it moves the number on this page.
      { id: 'marketing-brain', label: 'Marketing Brain', page: 'marketing-brain', icon: Sparkles },
      // Meta Campaigns folded into Social Media Builder → Paid tab (route kept alive).
      // Renamed 2026-09-14 (Jessica): the old name did not say what the page does.
      // She suggested "Social Media Ads" — deliberately NOT used, because this page
      // is not only ads: it has an Organic Posts tab beside Paid Ads, plus the
      // cross-platform Calendar and Performance tabs, and naming it Ads would hide
      // the organic half from everyone who looks for it here. Page id unchanged, so
      // routing and role_permissions rows are untouched.
      { id: 'meta-builder', label: 'Social Media', page: 'meta-builder' },
      { id: 'meta-invoices', label: 'Meta Invoices', page: 'meta-invoices' },
      { id: 'facebook-leads', label: 'Facebook Leads', page: 'facebook-leads' },
      { id: 'google-campaigns', label: 'Google Campaigns', page: 'google-campaigns' },
      { id: 'email-campaigns', label: 'Email Campaigns', page: 'email-campaigns' },
      // Fall Crab Photo Contest entries and "share your feast" photos: judge, pick
      // winners, send to the Media Library and Dotdigital (Greg, Oct 13, 2026).
      { id: 'customer-photos', label: 'Customer Photos', page: 'customer-photos' },
      { id: 'conversions', label: 'Conversions', page: 'conversions' },
      { id: 'conversion-settings', label: 'Conversion Settings', page: 'conversion-settings' },
      { id: 'marketing-studio', label: 'Marketing Studio', page: 'marketing-studio', icon: Sparkles },
      // Renamed 2026-09-14 (Jessica). She suggested "Blog Post Builder"; it builds
      // landing PAGES as well as blog posts (the brief has a Blog post / Landing page
      // switch and the renderer has a separate skeleton for each), so the name carries
      // both. Everything built through it so far happens to be a blog post, which is
      // why it reads as a blog tool.
      { id: 'content-builder', label: 'Blog & Page Builder', page: 'content-builder', icon: Sparkles },
      { id: 'video-studio', label: 'Video Studio', page: 'video-studio', icon: Sparkles },
      // Nested group (Greg, 2026-08-18): the three media pages live under one
      // expandable "Media Library" entry. The group itself has no page — the
      // landing page is the first child. Page ids are unchanged, so App.js
      // routing and role_permissions rows are untouched.
      {
        id: 'media-group', label: 'Media Library', icon: Image,
        children: [
          { id: 'media-library', label: 'Media Library', page: 'media-library' },
          { id: 'media-intake', label: 'Media Intake', page: 'media-intake' },
          { id: 'media-contact-sheet', label: 'Contact Sheets', page: 'media-contact-sheet' },
          { id: 'media-library-dedupe', label: 'Dedupe Review', page: 'media-library-dedupe' },
        ],
      },
      { id: 'youtube-upload', label: 'YouTube Upload', page: 'youtube-upload' },
      { id: 'cnc-event-copy', label: 'C&C Event Copy', page: 'cnc-event-copy' },
      { id: 'giphy-sync', label: 'Giphy', page: 'giphy-sync' },
      { id: 'call-center', label: 'Call Center Outreach', page: 'call-center' },
      { id: 'seo', label: 'SEO & AEO', page: 'seo' },
    ]
  },
  {
    id: 'bigcommerce', label: 'BigCommerce', icon: Store,
    allowedRoles: ['Admin'],
    children: [
      { id: 'bc-custom-fields', label: 'Custom Fields', page: 'bc-custom-fields' },
      { id: 'site-content', label: 'Site Content', page: 'site-content' },
      { id: 'bc-recipes', label: 'Recipes', page: 'bc-recipes' },
    ]
  },
  { id: 'mailing-list', label: 'Mailing List', icon: Mail, page: 'mailing-list', allowedRoles: ['Admin', 'Marketing'] },
  {
    id: 'comms', label: 'Communications', icon: Phone,
    allowedRoles: ['Admin', 'Manager', 'Customer Service'],
    children: [
      { id: 'contact-center-brain', label: 'Contact Center Brain', page: 'contact-center-brain', icon: Headphones, allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
      { id: 'contact-board', label: 'Contact Board', page: 'contact-board', icon: MessageSquare, allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
      { id: 'delay-tracker', label: 'Delay Tracker', page: 'delay-tracker', icon: Truck, allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
      { id: 'promo-codes', label: 'Promo Codes', page: 'promo-codes', icon: CreditCard, allowedRoles: ['Admin', 'Manager', 'Customer Service'] },
      { id: 'unified-inbox', label: 'Unified Inbox', page: 'unified-inbox', icon: Inbox },
      { id: 'ringcentral', label: 'RingCentral', page: 'ringcentral' },
    ]
  },
  { id: 'payroll', label: 'Payroll', icon: DollarSign, page: 'payroll', allowedRoles: ['Admin', 'HR'] },
  { id: 'time-clock', label: 'Time Clock', icon: Clock, page: 'time-clock', allowedRoles: ['Admin', 'IT', 'Manager'] },
  { id: 'tasks', label: 'Tasks', icon: Target, page: 'tasks', allowedRoles: ['Admin', 'IT', 'Manager', 'Marketing', 'HR', 'Procurement', 'Fulfillment', 'Customer Service', 'Accounting'] },
  // Top level on purpose: the To-Do section is ownerOnly (Greg alone), and the Team board is
  // the one board the whole Tuesday 9:30 room needs to reach. The server gates the data
  // anyway - team-board.js refuses anybody without a work_owners row - so a broad role list
  // here costs nothing and a narrow one would quietly hide the board from somebody in the room.
  { id: 'team-board', label: 'Team', icon: Users, page: 'team-board',
    allowedRoles: ['Admin', 'IT', 'Manager', 'Marketing', 'HR', 'Procurement', 'Fulfillment', 'Customer Service', 'Accounting'] },
  {
    id: 'todo', label: 'To-Do', icon: ClipboardCheck,
    allowedRoles: ['Admin', 'IT', 'Manager', 'Marketing', 'HR', 'Procurement', 'Fulfillment', 'Customer Service', 'Accounting'],
    children: [
      { id: 'todo-build', label: 'CP Build', page: 'todo-board', allowedStaff: ['Greg Cain'] },
      { id: 'todo-greg', label: 'Greg', page: 'todo-greg', icon: User, allowedStaff: ['Greg Cain'] },
      // Greg already has a board, so the shared one is hidden from him rather than sitting
      // beside his as a near-empty duplicate.
      { id: 'todo-mine', label: 'My Board', page: 'my-board', icon: User, hideForStaff: ['Greg Cain'] },
      { id: 'todo-team', label: 'Team', page: 'team-board', icon: Users },
      { id: 'todo-wish', label: 'Wish list', page: 'wish-list', icon: Lightbulb },
      { id: 'todo-plan', label: 'Order my day', page: 'todo-plan', icon: ListOrdered, allowedStaff: ['Greg Cain'] },
      { id: 'todo-delegation', label: 'Delegation', page: 'todo-delegation', icon: Users, allowedStaff: ['Greg Cain'] },
    ]
  },
  // Greg's personal books: a separate site (own database + passkeys) shown inside CPx.
  { id: 'household', label: 'Household', icon: Home, page: 'household', ownerOnly: true },
  { id: 'cameras', label: 'Cameras', icon: Camera, page: 'cameras', allowedRoles: ['Admin', 'Manager'] },
  { id: 'seventh-street', label: 'Seventh Street', icon: MapPin, allowedRoles: ['Admin', 'Manager', 'Seventh Street'], children: [
    { id: 'seventh-street-brain', label: 'Seventh Street Brain', page: 'seventh-street-brain', allowedRoles: ['Admin', 'Manager', 'Seventh Street'] },
  ]},
  { id: 'accounting', label: 'Accounting', icon: Calculator, allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement'], children: [
    { id: 'accounting-brain', label: 'Accounting Brain', page: 'accounting-brain', allowedRoles: ['Admin', 'Manager', 'Accounting'] },
    // Both kinds of bill on one screen (Greg, 2026-10-02). The two pages below stay
    // for the people who work only one side of it.
    { id: 'all-approvals', label: 'All Approvals', page: 'all-approvals', allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement'] },
    // The id stays 'approvals' so existing role_permissions rows keep applying; only the
    // label changed when Invoice Approvals arrived beside it (2026-09-17).
    { id: 'approvals', label: 'Crabber & Freight Approvals', page: 'approvals', allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement'] },
    { id: 'invoice-approvals', label: 'Invoice Approvals', page: 'invoice-approvals', allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement'] },
    // What each vendor sells us; the notes beside a bill are built from it (2026-10-08).
    { id: 'vendors', label: 'Vendors', page: 'vendors', allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement'] },
    // ⚠️ BY NAME, NOT BY ROLE. The buyers hold Admin and Accounting too, and Greg does
    // not want a buyer seeing the bank balance (2026-10-08). The server enforces the same
    // three people in cash-outlook.js; this only keeps the link out of everyone else's menu.
    { id: 'cash-outlook', label: 'Cash Outlook', page: 'cash-outlook', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Matt Cain', 'Mercedes Young'] },
    // The multi-year budget. Its Payroll tab lists everyone's pay, so it is by name too;
    // the server enforces the same two people in budget.js (2026-10-12).
    { id: 'budget', label: 'Budget', page: 'budget', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Mercedes Young'] },
    // The old QuickBooks Desktop scans matched to QuickBooks Online, for Mercedes to check.
    // By name: the scans include bank statements and payroll paperwork. The server enforces
    // the same two people in qbd-scans.js (2026-10-15).
    // The daily Authorize.net invoice and next-day payment, prepared from the settled batch
    // and posted when approved. It writes to the books, so by name; the server enforces the
    // same two people in authnet-qbo.js (2026-10-29).
    { id: 'authnet-deposits', label: 'Authorize.net Deposits', page: 'authnet-deposits', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Mercedes Young'] },
    { id: 'qbd-scans', label: 'QBD Scans', page: 'qbd-scans', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Mercedes Young'] },
    // Every employee's pay. By name, like Cash Outlook; the server enforces the same two
    // people in payroll.js (2026-10-11).
    { id: 'payroll-records', label: 'Payroll Records', page: 'payroll-records', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Mercedes Young'] },
    // Hours for each pay run, the pay calendar, the cash check and the season plan. Names
    // and pay, so by name; the server enforces the same two people in payroll-planner.js
    // (2026-10-13).
    { id: 'payroll-planner', label: 'Payroll Planner', page: 'payroll-planner', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Mercedes Young'] },
    // Time off, overtime, late/early, points and PIPs (Mercedes, 2026-10-29). Personnel
    // data, so by name; the server enforces the same two people in attendance.js, and
    // PIPs separately to the seven people Mercedes named (not Greg).
    // Round two (Mercedes, Nov 3, 2026): the managers open it too and see only their own team.
    // The server (attendance.js + lib/attendance-team.js ACCESS) is the real gate; keep in step.
    { id: 'attendance', label: 'Attendance', page: 'attendance', allowedRoles: ['Admin', 'Manager', 'Accounting', 'Procurement', 'Fulfillment'], allowedStaff: ['Greg Cain', 'Mercedes Young', 'Rachael Nelson', 'Carman Pilkerton', 'Matt Cain Jr', 'Brenda Hernandez', 'Montel Douglas'] },
    // What the buyer coach asks and how it weighs cash. By name, like Cash Outlook: it
    // decides what every buyer is told (2026-10-09).
    { id: 'coach-settings', label: 'Buying Coach Settings', page: 'coach-settings', allowedRoles: ['Admin', 'Manager', 'Accounting'], allowedStaff: ['Greg Cain', 'Matt Cain', 'Mercedes Young'] },
    { id: 'quickbooks', label: 'QuickBooks', page: 'quickbooks', allowedRoles: ['Admin', 'Manager', 'Accounting'] },
  ]},
  // Admin portal Tools and Lists (Azure exit WS8). The portal has no role guard on these screens.
  { id: 'tools-lists', label: 'Tools & Lists', icon: Wrench, children: [
    { id: 'shipping-holidays', label: 'Shipping Holidays', page: 'shipping-holidays' },
    { id: 'ups-blocklist', label: 'Blocked UPS Access Points', page: 'ups-blocklist' },
    { id: 'address-exceptions', label: 'Shipping Address Exceptions', page: 'address-exceptions' },
    { id: 'address-overrides', label: 'Shipping Address Overrides', page: 'address-overrides' },
    { id: 'special-charges', label: 'Special Charges', page: 'special-charges' },
    { id: 'unmapped-ignore', label: 'Unmapped Report Ignore List', page: 'unmapped-ignore' },
    { id: 'product-alert-config', label: 'Product Alert Config', page: 'product-alert-config' },
    { id: 'crab-suppliers', label: 'Crab Suppliers', page: 'crab-suppliers' },
    { id: 'set-vip-period', label: 'Set VIP Period', page: 'set-vip-period' },
    { id: 'set-vip-overlay', label: 'Set VIP Overlay', page: 'set-vip-overlay' },
    { id: 'fraud-check', label: 'Fraud Check', page: 'fraud-check' },
    { id: 'inventory-limit', label: 'Inventory Limit', page: 'inventory-limit' },
    // Tools & Lists end
  ]},
  { id: 'configure-site', label: 'Configure Site', icon: Settings, allowedRoles: ['Admin', 'Manager'], children: [
    { id: 'configure-sync', label: 'Sync & Logs', page: 'configure-site', allowedRoles: ['Admin'] },
    { id: 'configure-users', label: 'Users', page: 'configure-users', allowedRoles: ['Admin'] },
    { id: 'configure-audit-log', label: 'Auth Audit Log', page: 'audit-log', allowedRoles: ['Admin'] },
    { id: 'configure-security-alerts', label: 'Security Alerts', page: 'security-alerts', allowedRoles: ['Admin'] },
    // Pickup / last call times and the chase for unhandled order alerts (board 2020).
    { id: 'configure-email-config', label: 'Email Config', page: 'email-config', allowedRoles: ['Admin', 'Manager'] },
    { id: 'configure-order-alerts', label: 'Order Alert Settings', page: 'order-alerts', allowedRoles: ['Admin', 'Manager'] },
    { id: 'configure-roles', label: 'Role Permissions', page: 'configure-roles', allowedRoles: ['Admin'] },
    { id: 'banner-scheduler', label: 'Promo Banner', page: 'banner-scheduler', allowedRoles: ['Admin', 'Manager'] },
    { id: 'trouble-tickets', label: 'Trouble Tickets', page: 'trouble-tickets' },
    { id: 'jimmy-knowledge', label: 'Jimmy Knowledge', page: 'jimmy-knowledge', allowedRoles: ['Admin', 'Manager'] },
    { id: 'knowledge-capture', label: 'Knowledge Capture', page: 'knowledge-capture', allowedRoles: ['Admin', 'Manager'] },
    { id: 'checkout-telemetry', label: 'Telemetry', page: 'checkout-telemetry', allowedRoles: ['Admin', 'IT', 'Manager'] },
    { id: 'pdp-live-viewers', label: 'PDP Live Viewers', page: 'pdp-live-viewers', allowedRoles: ['Admin', 'IT', 'Manager'] },
    { id: 'checkout-funnel', label: 'Checkout Funnel', page: 'checkout-funnel', allowedRoles: ['Admin', 'IT', 'Manager'] },
    { id: 'traffic-analysis', label: 'Traffic Analysis', page: 'traffic-analysis', allowedRoles: ['Admin', 'Manager'] },
  ]},
  { id: 'help', label: 'Help', icon: BookOpen, allowedRoles: ['Admin', 'IT', 'Manager'], children: [
    { id: 'knowledge-base', label: 'Knowledge Base', page: 'knowledge-base' },
  ]},
];

function getParentId(page) {
  const parent = navItems.find(item => item.children?.some(
    c => c.page === page || c.children?.some(g => g.page === page)
  ));
  return parent?.id ?? null;
}

// Which nested sub-group (a child that itself has children) holds this page.
function getSubGroupId(page) {
  for (const item of navItems) {
    for (const child of item.children || []) {
      if (child.children?.some(g => g.page === page)) return child.id;
    }
  }
  return null;
}

// Cache permissions so we don't reload on every render
let _permCache = null;
let _permLoading = false;
let _permListeners = [];

function loadPermissions() {
  if (_permCache) return;
  if (_permLoading) return;
  _permLoading = true;
  neonQuery('role_permissions_load').then(rows => {
    // Build a map: { sectionId: Set(['Admin', 'Fulfillment', ...]) }
    const map = {};
    rows.forEach(r => {
      if (!map[r.section_id]) map[r.section_id] = new Set();
      map[r.section_id].add(r.role);
    });
    _permCache = map;
    _permListeners.forEach(fn => fn(map));
    _permListeners = [];
  }).catch(() => { _permLoading = false; });
}

export default function Sidebar({ currentPage, navigateTo, isOpen, onLogout, collapsed, onToggleCollapse }) {
  const [expandedId, setExpandedId] = useState(() => getParentId(currentPage));
  const [expandedSubId, setExpandedSubId] = useState(() => getSubGroupId(currentPage));
  const [rolePerms, setRolePerms] = useState(_permCache);
  const [navQuery, setNavQuery] = useState('');
  const user = getCurrentUser();

  // ── Favorites (Greg, 2026-09-23) ────────────────────────────────────────────────────
  // Works like Outlook's Favorites (Greg, 2026-10-09): starring a page adds a shortcut to
  // it at the top of the menu, just the link, not its section. The menu itself never
  // reorders, so the page is also still where it always was. Shortcuts list in menu order,
  // not by when they were starred. "Only favorites" hides the rest; the menu search still
  // finds every page.
  // Cached in localStorage per person so the menu does not jump on load, then confirmed
  // from the server, which is the source of truth so stars follow people to the phone.
  const favKey = `cpx-nav-favs-${user?.id || 'anon'}`;
  const [favState, setFavState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(favKey)) || { favorites: [], onlyFavorites: false }; }
    catch (_) { return { favorites: [], onlyFavorites: false }; }
  });
  const applyFavState = (next) => {
    setFavState(next);
    try { localStorage.setItem(favKey, JSON.stringify(next)); } catch (_) {}
  };
  useEffect(() => {
    let live = true;
    navFavorites('list')
      .then(r => { if (live) applyFavState({ favorites: r.favorites || [], onlyFavorites: !!r.onlyFavorites }); })
      .catch(() => {});
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favKey]);
  const favSet = useMemo(() => new Set(favState.favorites), [favState.favorites]);
  const onlyFavs = favState.onlyFavorites && favSet.size > 0;

  const toggleFav = (e, id) => {
    e.stopPropagation();
    const on = !favSet.has(id);
    const prev = favState;
    applyFavState({ ...favState, favorites: on ? [...favState.favorites, id] : favState.favorites.filter(x => x !== id) });
    navFavorites('set', { itemId: id, on })
      .then(r => applyFavState({ favorites: r.favorites || [], onlyFavorites: !!r.onlyFavorites }))
      .catch(() => applyFavState(prev));
  };
  const toggleOnlyFavs = () => {
    const on = !favState.onlyFavorites;
    const prev = favState;
    applyFavState({ ...favState, onlyFavorites: on });
    navFavorites('only', { on }).catch(() => applyFavState(prev));
  };
  const starFor = (id) => {
    const on = favSet.has(id);
    const label = on ? 'Remove from favorites' : 'Add to favorites';
    return (
      <span className={`nav-star${on ? ' on' : ''}`} onClick={(e) => toggleFav(e, id)} title={label} role="button" aria-label={label}>
        <Star size={12} fill={on ? 'currentColor' : 'none'} />
      </span>
    );
  };

  // Flat, permission-aware index of every reachable page, for the menu search.
  const searchIndex = useMemo(() => {
    const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
    const isAdminish = userRoles.includes('Admin') || userRoles.includes('IT');
    const roleOk = (node) => {
      if (isAdminish) return true;
      if (rolePerms && rolePerms[node.id]) return userRoles.some(r => rolePerms[node.id].has(r));
      if (node.allowedRoles) return userRoles.some(r => node.allowedRoles.includes(r));
      return true;
    };
    const out = [];
    navItems.forEach(item => {
      if (item.ownerOnly && user?.staffName !== 'Greg Cain') return;
      const parentOk = item.id === 'dashboard' || roleOk(item);
      if (item.page && parentOk) out.push({ id: item.id, label: item.label, page: item.page, parent: null });
      (item.children || []).forEach(child => {
        if (child.adminOnly && user?.staffName !== 'Greg Cain') return;
        if (child.allowedStaff && !child.allowedStaff.includes(user?.staffName)) return;
        if (child.hideForStaff && child.hideForStaff.includes(user?.staffName)) return;
        let childOk;
        if (isAdminish) childOk = true;
        else if (rolePerms && rolePerms[child.id]) childOk = userRoles.some(r => rolePerms[child.id].has(r));
        else if (child.allowedRoles) childOk = userRoles.some(r => child.allowedRoles.includes(r));
        else childOk = parentOk;
        if (!childOk) return;
        if (child.page) out.push({ id: child.id, label: child.label, page: child.page, parent: item.label });
        // Nested sub-groups (e.g. Marketing > Media Library > Dedupe Review)
        (child.children || []).forEach(g => {
          if (!g.page) return;
          let gOk;
          if (isAdminish) gOk = true;
          else if (rolePerms && rolePerms[g.id]) gOk = userRoles.some(r => rolePerms[g.id].has(r));
          else if (g.allowedRoles) gOk = userRoles.some(r => g.allowedRoles.includes(r));
          else gOk = true;
          if (gOk) out.push({ id: g.id, label: g.label, page: g.page, parent: `${item.label} · ${child.label}` });
        });
      });
    });
    return out;
  }, [user, rolePerms]);

  const q = navQuery.trim().toLowerCase();
  const searchResults = q
    ? searchIndex.filter(r => r.label.toLowerCase().includes(q) || (r.parent && r.parent.toLowerCase().includes(q))).slice(0, 14)
    : [];

  const goToResult = (r) => { setNavQuery(''); navigateTo(r.page); };

  // The shortcuts at the top of the menu: every starred page this person can still reach.
  const favLinks = searchIndex.filter(r => r.id !== 'dashboard' && favSet.has(r.id));
  // "Only favorites" leaves just the shortcuts, plus the section holding the page you are
  // standing on when that page is not one of them (reached via search, say).
  const currentIsFav = favLinks.some(r => r.page === currentPage);
  const holdsCurrent = (node) => node.page === currentPage || !!(node.children && node.children.some(holdsCurrent));
  const keep = (node) => !onlyFavs || node.id === 'dashboard' || (!currentIsFav && holdsCurrent(node));

  useEffect(() => {
    if (_permCache) { setRolePerms(_permCache); return; }
    _permListeners.push(map => setRolePerms(map));
    loadPermissions();
  }, []);

  useEffect(() => {
    const parentId = getParentId(currentPage);
    if (parentId) setExpandedId(parentId);
    const subId = getSubGroupId(currentPage);
    if (subId) setExpandedSubId(subId);
  }, [currentPage]);

  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);
  const toggleSubExpand = (id) => setExpandedSubId(prev => prev === id ? null : id);

  return (
    <div className={`sidebar${isOpen ? ' mobile-open' : ''}${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-logo" style={{ flexDirection: 'column', alignItems: 'center', gap: 0 }}>
        {collapsed ? (
          <img src={logoCp} alt="Crab Place logo" style={{ width: 36 }} />
        ) : (
          <>
            <img src={logoCp} alt="Crab Place logo" style={{ width: 160 }} />
            <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--orange)', letterSpacing: '1px' }}>ADMIN PORTAL</div>
          </>
        )}
      </div>

      {!collapsed && (
        <div className="nav-search has-fav-toggle">
          <Search size={13} className="nav-search-icon" />
          <input
            className="nav-search-input"
            value={navQuery}
            onChange={(e) => setNavQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && searchResults[0]) goToResult(searchResults[0]);
              if (e.key === 'Escape') setNavQuery('');
            }}
            placeholder="Search menu..."
          />
          {navQuery && <X size={13} className="nav-search-clear" onClick={() => setNavQuery('')} />}
          <button
            type="button"
            className={`nav-fav-toggle${favState.onlyFavorites ? ' on' : ''}`}
            onClick={toggleOnlyFavs}
            title={favState.onlyFavorites ? 'Showing favorites only. Click to show everything.' : 'Show favorites only'}
          >
            <Star size={14} fill={favState.onlyFavorites ? 'currentColor' : 'none'} />
          </button>
          {favState.onlyFavorites && favSet.size === 0 && !q && (
            <div className="nav-fav-hint">Click the star beside a page to add it to your favorites.</div>
          )}
          {q && (
            <div className="nav-search-results">
              {searchResults.length === 0 && <div className="nav-search-empty">No matches</div>}
              {searchResults.map((r, i) => (
                <div key={`${r.page}-${i}`} className="nav-search-result" onClick={() => goToResult(r)}>
                  <span className="nav-search-result-label">{r.label}</span>
                  {r.parent && <span className="nav-search-result-parent">{r.parent}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {navItems.filter(item => {
        // Owner-only: only Greg Cain sees these (personal items like To-Do, Cameras)
        if (item.ownerOnly && user?.staffName !== 'Greg Cain') return false;
        // Dashboard is always visible
        if (item.id === 'dashboard') return true;
        // Admin and IT roles have access to everything
        const userRoles = user?.roles || [];
        if (userRoles.includes('Admin') || userRoles.includes('IT')) return true;
        // Dynamic role permissions from Neon (if loaded)
        if (rolePerms && rolePerms[item.id]) {
          if (!userRoles.some(r => rolePerms[item.id].has(r))) {
            // Parent denied — but check if user has access to any child
            if (item.children) {
              const hasChildAccess = item.children.some(child => rolePerms[child.id] && userRoles.some(r => rolePerms[child.id].has(r)));
              if (hasChildAccess) return true;
            }
            return false;
          }
        } else if (item.allowedRoles) {
          // Fallback to hardcoded while permissions load
          if (!userRoles.some(r => item.allowedRoles.includes(r))) return false;
        }
        return true;
      }).filter(keep).map(item => {
        const Icon = item.icon;
        // In "only favorites" mode every section stays open: the menu IS the short list.
        const isExpanded = onlyFavs || expandedId === item.id;
        const hasChildren = item.children && item.children.length > 0;
        const isActive = item.page === currentPage || (hasChildren && item.children.some(c => c.page === currentPage));

        return (
          <div key={item.id}>
            <div
              className={`nav-item ${isActive ? 'active' : ''}`}
              style={isActive ? { fontWeight: 700, color: 'var(--orange)' } : {}}
              onClick={() => hasChildren ? toggleExpand(item.id) : navigateTo(item.page)}
              title={collapsed ? item.label : undefined}
            >
              <div className="nav-item-icon">
                <Icon size={15} />
                {!collapsed && item.label}
                {!collapsed && item.page && <PortNavBadge page={item.page} />}
              </div>
              {!collapsed && hasChildren && (isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />)}
              {!collapsed && !hasChildren && item.id !== 'dashboard' && starFor(item.id)}
            </div>
            {!collapsed && hasChildren && isExpanded && (
              <div className="nav-sub">
                {item.children.filter(child => {
                  if (child.adminOnly && user?.staffName !== 'Greg Cain') return false;
                  if (child.allowedStaff && !child.allowedStaff.includes(user?.staffName)) return false;
                  if (child.hideForStaff && child.hideForStaff.includes(user?.staffName)) return false;
                  const ur = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
                  if (ur.includes('Admin') || ur.includes('IT')) return true;
                  // Dynamic child-level permissions from Neon
                  if (rolePerms && rolePerms[child.id]) {
                    if (!ur.some(r => rolePerms[child.id].has(r))) return false;
                  } else if (child.allowedRoles) {
                    if (!ur.some(r => child.allowedRoles.includes(r))) return false;
                  }
                  return true;
                }).filter(keep).map(child => {
                  const hasSub = child.children && child.children.length > 0;
                  if (!hasSub) {
                    return (
                      <div
                        key={child.id}
                        className={`nav-sub-item nav-sub-row ${currentPage === child.page ? 'active' : ''}`}
                        style={currentPage === child.page ? { fontWeight: 700, color: 'var(--orange)' } : {}}
                        onClick={() => navigateTo(child.page)}
                      >
                        <span>{child.label}<PortNavBadge page={child.page} /></span>
                        {starFor(child.id)}
                      </div>
                    );
                  }
                  const subExpanded = onlyFavs || expandedSubId === child.id;
                  const subActive = child.children.some(g => g.page === currentPage);
                  return (
                    <div key={child.id}>
                      <div
                        className={`nav-sub-item ${subActive ? 'active' : ''}`}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...(subActive ? { fontWeight: 700, color: 'var(--orange)' } : {}) }}
                        onClick={() => toggleSubExpand(child.id)}
                      >
                        <span>{child.label}</span>
                        {subExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </div>
                      {subExpanded && child.children.filter(keep).map(g => (
                        <div
                          key={g.id}
                          className={`nav-sub-item nav-sub-row ${currentPage === g.page ? 'active' : ''}`}
                          style={{ paddingLeft: 34, ...(currentPage === g.page ? { fontWeight: 700, color: 'var(--orange)' } : {}) }}
                          onClick={() => navigateTo(g.page)}
                        >
                          <span>{g.label}<PortNavBadge page={g.page} /></span>
                          {starFor(g.id)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            {!collapsed && item.id === 'dashboard' && favLinks.length > 0 && (
              <div className="nav-favs">
                {favLinks.map(r => (
                  <div
                    key={r.id}
                    className={`nav-sub-item nav-sub-row nav-fav-link ${currentPage === r.page ? 'active' : ''}`}
                    style={currentPage === r.page ? { fontWeight: 700, color: 'var(--orange)' } : {}}
                    onClick={() => navigateTo(r.page)}
                    title={r.parent || undefined}
                  >
                    <span>{r.label}<PortNavBadge page={r.page} /></span>
                    {starFor(r.id)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="sidebar-collapse-toggle" onClick={onToggleCollapse} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
        {collapsed ? '»' : '«'}
      </div>

      <div className="sidebar-user" style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between' }}>
        {collapsed ? (
          <div className="user-avatar" title={user?.staffName || 'Admin'}>{user?.staffName?.split(' ').map(n => n[0]).join('') || 'GC'}</div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="user-avatar">{user?.staffName?.split(' ').map(n => n[0]).join('') || 'GC'}</div>
              <span>{user?.staffName || 'Admin'}</span>
            </div>
            {onLogout && (
              <button onClick={onLogout} title="Sign out" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 4, display: 'flex', alignItems: 'center' }}>
                <LogOut size={15} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
